<script lang="ts">
    import { onDestroy, onMount } from 'svelte'
    import { derived as sderived, writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { isEqual, orderBy } from 'lodash-es'

    import { iconMap } from 'hydria/icons'
    import { gettext as _, ngettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { session, simpleFetch, groups as ugroups } from 'hydria/services/session'
    import { sanchor, sgoto, spath, sreplace } from 'hydria/svelte_helpers'
    import * as rt from 'hydria/utils/route'
    import { querystring } from 'hydria/utils/route'
    import { toLuxon } from 'hydria/utils/time'

    import { Breadcrumb } from 'breadcrumb'

    import Date from 'hydria/components/Date.svelte'
    import Dropdown from 'hydria/components/Dropdown.svelte'
    import PageList from 'hydria/components/PageList.svelte'
    import ExtraFilters from './ExtraFilters.svelte'
    import TokenAnchorProgress from './User/TokenAnchorProgress.svelte'

    const LIMIT = 10
    const DELAY_MS = 1250

    // Route parameters
    const mapping: rt.ISimpleMapping = {
        q: 'string',
        skip: 'int1+',
        hastechnicaldelegs: '3state',
        istechnicaldeleg: '3state',
        hasrenewaldelegs: '3state',
        isrenewaldeleg: '3state',
        hascity: '3state',
        locked: '3state',
        active: '3state',

        has_subaccounts: '3state',
        is_subaccount: '3state',

        has_zone_price: '3state',
        has_tax_exemption: '3state',
        has_tld_price: '3state',
        has_admin_zone_slots: '3state',
        has_pmethod: 'boolstring',

        has_recovery: '3state',
        has_passkey: '3state',
        has_twofa: 'boolstring',
        has_api_token: '3state',
    }
    const mappingOrder = ['q', 'skip']

    // Search options for the backend
    interface SOptions {
        q: string
        skip: number
        hastechnicaldelegs: null | boolean
        istechnicaldeleg: null | boolean
        hasrenewaldelegs: null | boolean
        isrenewaldeleg: null | boolean
        hascity: null | boolean
        locked: null | boolean
        active: null | boolean

        has_subaccounts: null | boolean
        is_subaccount: null | boolean

        has_zone_price: null | boolean
        has_tax_exemption: null | boolean
        has_tld_price: null | boolean
        has_admin_zone_slots: null | boolean
        has_pmethod: null | boolean | string

        has_recovery: null | boolean
        has_passkey: null | boolean
        has_twofa: null | boolean | string
        has_api_token: null | boolean

        [key: string]: any
    }
    const defaults: SOptions = {
        q: '',
        skip: 0,
        hastechnicaldelegs: null,
        istechnicaldeleg: null,
        hasrenewaldelegs: null,
        isrenewaldeleg: null,
        hascity: null,
        locked: null,
        active: null,

        has_subaccounts: null,
        is_subaccount: null,

        has_zone_price: null,
        has_tax_exemption: null,
        has_tld_price: null,
        has_admin_zone_slots: null,
        has_pmethod: null,

        has_recovery: null,
        has_passkey: null,
        has_twofa: null,
        has_api_token: null,
    }

    // Search result from the server
    interface ISearchResult {
        items: Array<any>
        total_items: number
    }

    // search options; kept in sync with the query string (bi-directional)
    let sOptions: SOptions = $state(Object.assign({}, defaults))
    // search options parsed from the querystring
    let loadedOptions: SOptions = Object.assign({}, defaults)

    // Set to true after the *first* query string load
    let queryStringLoaded = false

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    function setQueryString(inOptions: SOptions) {
        if (!queryStringLoaded) return

        if (isEqual(inOptions, loadedOptions)) return

        const options = { ...inOptions }

        if (options.skip && options.skip == loadedOptions.skip) {
            // Something non-skip must have changed, so we force skip to 0
            options.skip = 0
        }

        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/wdom-users' : `/wdom-users?${qs}`
            // If something other than skip has changed, we also want to enforce skip = 0
            sgoto(path)
        }
    }
    function cleanIncomingQueryString(options: SOptions) {
        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/wdom-users' : `/wdom-users?${qs}`
            // First time the page loads, and the parameters are incorrect.
            // (e.g. "q=", "skip=0", wrong parameter order, and so on)
            console.warn('parameters are dirty, replacing history from', $querystring, 'to', qs)
            sreplace(path)
        }
    }

    function loadQueryString(qs: string) {
        let sMap = rt.simpleParseQs(qs, rt.simpleMap(mapping))
        loadedOptions = Object.assign({}, defaults, Object.fromEntries(sMap))

        // So we only trigger the reactivity on true change, in case we are about to replace()
        if (!isEqual(sOptions, loadedOptions)) {
            sOptions = { ...loadedOptions }
        }

        // We also want to keep the URL clean.
        if (!queryStringLoaded) {
            cleanIncomingQueryString(loadedOptions)
        }
        loadIntoForm()
        queryStringLoaded = true
        doSearch()
    }

    interface IExtraMetaField {
        name: string
        tag: string
        type: 'choice'
        choices: {
            tag: string
            text: string
        }[]
    }
    // let extraMetaConfig: any
    let extraMetaFields: IExtraMetaField[] = $state([])
    let extraReady = $state(false)

    async function loadExtraMetaConfig() {
        let r: any
        try {
            r = await simpleFetch('GET', '/json/wdom-user/1/extra-meta/config')
            // extraMetaConfig = r.data.config
            extraMetaFields = orderBy(
                r.data.fields.filter((d) => d.type == 'choice'),
                [(d) => r.data.field_order.indexOf(d.tag)],
                ['asc'],
            )
            for (const f of extraMetaFields) {
                f.choices.unshift({ tag: '!', text: '-- Sans réponse --' })
            }

            for (const emf of extraMetaFields) {
                mapping[`em-${emf.tag}`] = 'string'
                defaults[`em-${emf.tag}`] = ''
            }
            extraReady = true
        } catch (e) {
            console.error('Error loading extra meta:', e)
            notify({
                type: 'danger',
                title: 'Meta data',
                text: 'Erreur de chargement des méta données additionnelles',
            })
            // extraMetaConfig = {}
            extraMetaFields = []
        }
    }

    onMount(() => {
        unsubs.push(
            querystring.subscribe(($qs) => {
                if (extraReady) loadQueryString($qs ? $qs : '')
            }),
        )
    })

    async function setup() {
        await loadExtraMetaConfig()
        loadQueryString($querystring)
    }
    setup()

    let results = writable([] as any[])

    let updating = $state(false)
    let loading = $state(true)
    let searchId = 0
    let searchQueryStringJson: string | undefined = $state()
    let searchQueryString = writable('')
    function doSearch() {
        updating = true

        let sdata: { [key: string]: any } = {}

        if (sOptions.q != '') sdata.text = sOptions.q
        for (const [field, value] of Object.entries(sOptions)) {
            if (['q', 'skip'].includes(field)) continue
            switch (mapping[field]) {
                case '3state':
                case 'boolean':
                case 'boolstring':
                    if (value != null) sdata[field] = value
                    break
                case 'string':
                    if (value != null && value != '') sdata[field] = value
                    break
            }
        }
        $searchQueryString = rt.simpleMapToQs(new Map(Object.entries(sOptions)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder), ['skip']).toString()
        searchQueryStringJson = encodeURIComponent(JSON.stringify(sdata))
        sdata.limit = LIMIT
        sdata.with_delegate_data = '1'
        sdata.sort_by = 'email'
        sdata.sort_asc = true
        sdata.with_ecom_basics = true
        sdata.with_signin_opts = true
        if (sOptions.skip > 0) sdata.skip = sOptions.skip

        sdata.with_api_token_count = '1'

        searchId++
        ;((localSearchId) => {
            simpleFetch('GET', '/json/wdom-user/1/', { data: JSON.stringify(sdata) })
                .then((r) => {
                    if (localSearchId != searchId) {
                        // This result does not match the current search anymore
                        // and there should be another search in progress
                        console.warn('Received search result for an older search, ignoring')
                        return
                    }
                    loadSearchResult(r.data as ISearchResult)
                    updating = false
                })
                .catch((e) => {
                    console.error('Error loading search results:', e)
                    notify({ title: $_('Search'), text: $_('Failed to load search results'), type: 'danger' })
                    updating = false
                })
        })(searchId)
    }

    let nItems = $state(0)

    function loadSearchResult(data: ISearchResult) {
        let litems: any[] = []

        nItems = data.total_items

        for (const item of data.items) {
            if (item['ctime']) item['ctime'] = toLuxon(item['ctime'])

            litems.push(item)
        }

        results.set(litems)
        if (loading) loading = false
    }

    // svelte-ignore state_referenced_locally
    let text = writable(sOptions.q)

    function loadIntoForm() {
        $text = sOptions.q
    }

    let textTimer: any = null

    function clearDelayTimer() {
        if (textTimer != null) {
            clearTimeout(textTimer)
            textTimer = null
        }
    }

    unsubs.push(
        text.subscribe((newValue) => {
            clearDelayTimer()
            if (newValue == sOptions.q) {
                return
            }
            textTimer = setTimeout(() => {
                sOptions.q = newValue
                textTimer = null
                setQueryString(sOptions)
            }, DELAY_MS)
        }),
    )

    function submit() {
        clearDelayTimer()
        sOptions.q = $text
        setQueryString(sOptions)
    }
    let mainInput: HTMLInputElement | undefined = $state()
    onMount(() => {
        if (mainInput) mainInput.focus()
    })

    const crumbs = sderived(searchQueryString, ($searchQueryString) => [
        { txt: $_('Utilisateurs'), url: spath('/wdom-users') },
        { txt: iconMap, html: true, urlClasses: 'btn btn-outline-secondary btn-sm', url: spath('/wdom-users/map' + ($searchQueryString ? `?${$searchQueryString}` : '')), extra: true },
    ])

    function makeTldList(tldList: string[]) {
        let l = [...tldList]
        l.sort()
        if (l.length > 4) {
            l = l.slice(3)
            l.push('…')
        }
        return l.join(', ')
    }

    const methodTranslation = {
        'credit card': 'Carte de crédit',
        stripe: 'Stripe',
        quotation: 'Soumission',
        'bill later': 'Paiement différé',
    }
    function makeMethodList(methods: string[]) {
        if (!methods || methods.length < 1) return '–'
        return methods.map((m) => (methodTranslation[m] ? methodTranslation[m] : '????')).join(', ')
    }

    let showFilters = $state(false)
</script>

<style>
    #wdom-user-table {
        /* Structure..
            1er: mot "Délégué" flottant pour afficher au dessus des 2 derniere cols
            2..9, 8 colonnes d'entete
            10 + cells, 8 par ligne
        */

        /* La 1ere cell de chaque row a la classe wut-c1 */

        display: grid;
        grid-template-rows: auto 17px repeat(40, auto);
        grid-template-columns: 2fr 4fr 7.5em repeat(3, minmax(6em, 1fr)) auto auto auto;
        gap: 0;
        /* padding: 1ex 1em; */
        text-align: center;
    }
    @media (min-width: 1400px) {
        #wdom-user-table {
            grid-template-columns: 2fr 4fr 12em 7.5em repeat(3, minmax(6em, 1fr)) auto auto auto;
        }
    }
    @media (min-width: 1600px) {
        #wdom-user-table {
            grid-template-columns: 2fr 4fr 7em 12em 7.5em repeat(3, minmax(6em, 1fr)) auto auto auto;
        }
    }
    @media (min-width: 1850px) {
        #wdom-user-table {
            grid-template-columns: 2fr 4fr 7em 7em 12em 7.5em repeat(3, minmax(6em, 1fr)) auto auto auto;
        }
    }
    #wdom-user-table > *.wut-h {
        font-weight: bold;
        align-self: end;
        grid-row: 1 / span 2;
    }

    #wdom-user-table > *.wut-h:nth-child(11),
    #wdom-user-table > *.wut-h:nth-child(12) {
        grid-row: 2 / span 1;
    }
    #wdom-user-table > *.wut-h.deleg-title {
        grid-column: 7 / span 2;
        grid-row: 1 / span 1;
        align-self: end;
        justify-self: center;
    }

    #wdom-user-table > *.row-even {
        background-color: #f9fafa;
    }

    #wdom-user-table > * {
        padding: 0.75ex 0.5em;
    }

    #wdom-user-table > .wut-c1 {
        grid-column: 1;
    }

    /* toutes les cells passé header */
    #wdom-user-table > *:nth-child(n + 14) {
        border-top: 1px solid #eee;
    }

    /* Cols...
     * 10n+2:  Col 1, Nom
     * 10n+3:  Col 2, Email
     * 10n+4:  Col 3, Signin possibilities
     * 10n+5:  Col 4, Ecom icons/properties
     * 10n+6:  Col 5, Tags
     * 10n+7:  Col 6, Création
     * 10n+8:  Col 7, Domains
     * 10n+9:  Col 8, Zones
     * 10n+10:  Col 9, Slots
     * 10n+11:  Col 10, Deleg (is)
     * 10n+12:  Col 11, Deleg (have)
     * 10k+13:  Col 12, Subaccount
     */

    #wdom-user-table > *:nth-child(12n + 2),
    #wdom-user-table > *:nth-child(12n + 3),
    #wdom-user-table > *:nth-child(12n + 6) {
        text-align: left;
    }

    #wdom-user-table > *:nth-child(12n + 11),
    #wdom-user-table > *:nth-child(12n + 12) {
        white-space: nowrap;
        word-wrap: normal;
    }

    #wdom-user-table > *:nth-child(12n + 2),
    #wdom-user-table > *:nth-child(12n + 3) {
        overflow: hidden;
        text-overflow: ellipsis;
        word-wrap: break-word;
        white-space: normal;
    }

    #wdom-user-table > * .extra {
        display: none;
    }
    #wdom-user-table .tags,
    #wdom-user-table .systags,
    .signopts {
        display: none;
    }
    #wdom-user-table .wut-h.systags,
    #wdom-user-table .wut-h.signopts {
        border-bottom: 1px solid #777;
        border-bottom-left-radius: 5px;
        border-bottom-right-radius: 5px;
    }

    /* Version plus mince des délégués */
    @media (min-width: 1600px) {
        #wdom-user-table > * .extra {
            display: initial;
        }
        #wdom-user-table > *.wut-h.deleg-title {
            display: none;
        }
    }

    /* Afficher les tags */
    @media (min-width: 1400px) {
        #wdom-user-table .tags {
            display: initial;
        }
    }
    @media (min-width: 1600px) {
        #wdom-user-table .tags,
        #wdom-user-table .systags {
            display: initial;
        }
    }
    @media (min-width: 1850px) {
        #wdom-user-table .signopts {
            display: initial;
        }
    }

    .search-extra-meta-container {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        margin: 0.25rem -0.25em 0;
    }
    .search-extra-meta-container > * {
        flex: 1 1 auto;
        max-width: 33%;
        min-width: 25%;
        padding: 0.25em;
    }

    .text-very-muted {
        color: #eee;
    }

    .tags {
        max-width: 12rem;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .tags .enum span {
        white-space: nowrap;
    }

    .text-col {
        min-width: 15rem;
    }

    /* .systags {
        display: grid;
    } */
    .systags > span {
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        align-items: center;
        justify-items: center;
    }
    /* .signopts {
        display: grid;
    } */
    .signopts > span {
        display: grid;
        grid-template-columns: repeat(6, 1fr);
        align-items: center;
        justify-items: center;
    }

    .overlap {
        display: grid;
        align-items: center;
        justify-items: center;
    }
    .overlap > :global(*) {
        grid-column: 1/2;
        grid-row: 1/2;
    }
    .overlap > :global(*):first-child {
        opacity: 0.75;
    }

    .is-subaccount {
        color: #088;
    }
    .has-subaccounts {
        color: #808;
    }
</style>

<Breadcrumb {crumbs} />

<div class="card mb-4">
    <div class="card-title text-primary">{$_('Search parameters')}</div>
    <div class="card-body">
        <form
            onsubmit={(e) => {
                e.preventDefault()
                submit()
            }}
        >
            <div class="d-flex gap-2 flex-wrap">
                <div class="col text-col">
                    <div class="input-group">
                        <input type="text" class="form-control" bind:this={mainInput} bind:value={$text} />
                        {#if $text}
                            <button
                                class="btn btn-secondary"
                                type="button"
                                disabled={updating}
                                onclick={() => {
                                    $text = ''
                                    submit()
                                }}><Icon inline icon="fa:times" /></button
                            >
                        {/if}
                        <button type="button" class="btn btn-outline-secondary" onclick={() => (showFilters = true)}><Icon inline icon="fa:filter" /></button>
                        <button class="btn btn-secondary" type="submit" disabled={updating}>{$_('Filter')}</button>
                    </div>
                </div>
                <div class="btn-group">
                    <button type="button" class="btn btn-secondary" disabled>Tech :</button>
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        data-tooltip="A des délégués"
                        disabled={updating}
                        class:active={sOptions.hastechnicaldelegs === true}
                        onclick={() => {
                            sOptions.hastechnicaldelegs = sOptions.hastechnicaldelegs === true ? null : true
                            setQueryString(sOptions)
                        }}><Icon inline icon="pajamas:work-item-parent" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        data-tooltip="Est un délégué"
                        disabled={updating}
                        class:active={sOptions.istechnicaldeleg === true}
                        onclick={() => {
                            sOptions.istechnicaldeleg = sOptions.istechnicaldeleg === true ? null : true
                            setQueryString(sOptions)
                        }}><Icon inline icon="pajamas:work-item-child" /></button
                    >
                </div>
                <div class="btn-group">
                    <button type="button" class="btn btn-secondary" disabled>Renew :</button>
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        data-tooltip="A des délégués"
                        disabled={updating}
                        class:active={sOptions.hasrenewaldelegs === true}
                        onclick={() => {
                            sOptions.hasrenewaldelegs = sOptions.hasrenewaldelegs === true ? null : true
                            setQueryString(sOptions)
                        }}><Icon inline icon="pajamas:work-item-parent" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        data-tooltip="Est un délégué"
                        disabled={updating}
                        class:active={sOptions.isrenewaldeleg === true}
                        onclick={() => {
                            sOptions.isrenewaldeleg = sOptions.isrenewaldeleg === true ? null : true
                            setQueryString(sOptions)
                        }}><Icon inline icon="pajamas:work-item-child" /></button
                    >
                </div>
                <div class="btn-group">
                    <button type="button" class="btn btn-secondary" disabled><Icon inline icon="mdi:account" /></button>
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        data-tooltip="A des sous-comptes"
                        disabled={updating}
                        class:active={sOptions.has_subaccounts === true}
                        onclick={() => {
                            sOptions.has_subaccounts = sOptions.has_subaccounts === true ? null : true
                            if (sOptions.has_subaccounts) sOptions.is_subaccount = null
                            setQueryString(sOptions)
                        }}><Icon inline icon="pajamas:work-item-parent" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        data-tooltip="Est un sous-compte"
                        disabled={updating}
                        class:active={sOptions.is_subaccount === true}
                        onclick={() => {
                            sOptions.is_subaccount = sOptions.is_subaccount === true ? null : true
                            if (sOptions.is_subaccount) sOptions.has_subaccounts = null
                            setQueryString(sOptions)
                        }}><Icon inline icon="pajamas:work-item-child" /></button
                    >
                </div>
                <div class="btn-group">
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        data-tooltip="Avec un nom de ville"
                        disabled={updating}
                        class:active={sOptions.hascity === true}
                        onclick={() => {
                            sOptions.hascity = sOptions.hascity === true ? null : true
                            setQueryString(sOptions)
                        }}><Icon inline icon="fa:address-card" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        data-tooltip="Sans un nom de ville"
                        disabled={updating}
                        class:active={sOptions.hascity === false}
                        onclick={() => {
                            sOptions.hascity = sOptions.hascity === false ? null : false
                            setQueryString(sOptions)
                        }}><Icon inline icon="fa:address-card-o" /></button
                    >
                </div>
                <div class="btn-group">
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        data-tooltip={'Compte blocké\n(trop de tentatives,\nmauvais mot de passe)'}
                        disabled={updating}
                        class:active={sOptions.locked === true}
                        onclick={() => {
                            sOptions.locked = sOptions.locked === true ? null : true
                            setQueryString(sOptions)
                        }}><Icon inline icon="fa:lock" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        data-tooltip="Compte non-blocké"
                        disabled={updating}
                        class:active={sOptions.locked === false}
                        onclick={() => {
                            sOptions.locked = sOptions.locked === false ? null : false
                            setQueryString(sOptions)
                        }}><Icon inline icon="fa:unlock-alt" /></button
                    >
                </div>
                <div class="btn-group">
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        data-tooltip={'Compte actif\n(peut se connecter)'}
                        disabled={updating}
                        class:active={sOptions.active === true}
                        onclick={() => {
                            sOptions.active = sOptions.active === true ? null : true
                            setQueryString(sOptions)
                        }}><Icon inline icon="fa:thumbs-up" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary tt-left"
                        data-tooltip={'Compte inactif\n(ne peut pas se connecter)'}
                        disabled={updating}
                        class:active={sOptions.active === false}
                        onclick={() => {
                            sOptions.active = sOptions.active === false ? null : false
                            setQueryString(sOptions)
                        }}><Icon inline icon="fa:thumbs-down" /></button
                    >
                </div>
            </div>
            <div class="d-flex gap-2 flex-wrap mt-2">
                <!-- Tax – Exemption -->
                <div class="btn-group">
                    <button
                        type="button"
                        class="btn btn-outline-secondary tt-below"
                        data-tooltip={'Taxes normales'}
                        disabled={updating}
                        class:active={sOptions.has_tax_exemption === false}
                        onclick={() => {
                            sOptions.has_tax_exemption = sOptions.has_tax_exemption === false ? null : false
                            setQueryString(sOptions)
                        }}><Icon inline icon="tabler:tax" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary overlap tt-below"
                        data-tooltip={'Sans taxation'}
                        disabled={updating}
                        class:active={sOptions.has_tax_exemption === true}
                        onclick={() => {
                            sOptions.has_tax_exemption = sOptions.has_tax_exemption === true ? null : true
                            setQueryString(sOptions)
                        }}><Icon inline icon="tabler:tax" /><Icon inline icon="lucide:slash" /></button
                    >
                </div>
                <!-- Zone – Custom Price -->
                <div class="btn-group">
                    <button
                        type="button"
                        class="btn btn-outline-secondary tt-below"
                        data-tooltip={'Prix spécial de zone'}
                        disabled={updating}
                        class:active={sOptions.has_zone_price === true}
                        onclick={() => {
                            sOptions.has_zone_price = sOptions.has_zone_price === true ? null : true
                            setQueryString(sOptions)
                        }}><Icon inline icon="mdi:database" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary overlap tt-below"
                        data-tooltip={'Prix normal de zone'}
                        disabled={updating}
                        class:active={sOptions.has_zone_price === false}
                        onclick={() => {
                            sOptions.has_zone_price = sOptions.has_zone_price === false ? null : false
                            setQueryString(sOptions)
                        }}><Icon inline icon="mdi:database" /><Icon inline icon="lucide:slash" /></button
                    >
                </div>
                <!-- Zone – Admin Slots -->
                <div class="btn-group">
                    <button
                        type="button"
                        class="btn btn-outline-secondary tt-below"
                        data-tooltip={'Publication arbitraire de zone (« admin » zone slots)'}
                        disabled={updating}
                        class:active={sOptions.has_admin_zone_slots === true}
                        onclick={() => {
                            sOptions.has_admin_zone_slots = sOptions.has_admin_zone_slots === true ? null : true
                            setQueryString(sOptions)
                        }}><Icon inline icon="lucide-lab:slot-card-credit" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary overlap tt-below"
                        data-tooltip={'Sans publication arbitraire de zone (pas de « admin » zone slots)'}
                        disabled={updating}
                        class:active={sOptions.has_admin_zone_slots === false}
                        onclick={() => {
                            sOptions.has_admin_zone_slots = sOptions.has_admin_zone_slots === false ? null : false
                            setQueryString(sOptions)
                        }}><Icon inline icon="lucide-lab:slot-card-credit" /><Icon inline icon="lucide:slash" /></button
                    >
                </div>
                <!-- Prix de TLD -->
                <div class="btn-group">
                    <button
                        type="button"
                        class="btn btn-outline-secondary tt-below"
                        data-tooltip={'Prix spéciaux selon le TLD'}
                        disabled={updating}
                        class:active={sOptions.has_tld_price === true}
                        onclick={() => {
                            sOptions.has_tld_price = sOptions.has_tld_price === true ? null : true
                            setQueryString(sOptions)
                        }}><Icon inline icon="fa:globe" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary overlap tt-below"
                        data-tooltip={'Prix de domaines normaux'}
                        disabled={updating}
                        class:active={sOptions.has_tld_price === false}
                        onclick={() => {
                            sOptions.has_tld_price = sOptions.has_tld_price === false ? null : false
                            setQueryString(sOptions)
                        }}><Icon inline icon="fa:globe" /><Icon inline icon="lucide:slash" /></button
                    >
                </div>

                <!-- Méthodes de paiement -->
                <div class="btn-group">
                    <button type="button" class="btn btn-secondary" disabled><Icon inline icon="mdi:dollar" /></button>
                    <button
                        type="button"
                        class="btn btn-outline-secondary tt-below"
                        data-tooltip={'Sans configuration spéciale\n(Méthode par défaut seulement)'}
                        disabled={updating}
                        class:active={sOptions.has_pmethod === false}
                        onclick={() => {
                            sOptions.has_pmethod = sOptions.has_pmethod === false ? null : false
                            setQueryString(sOptions)
                        }}><Icon inline icon="mdi:do-not-disturb-alt" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary tt-below"
                        data-tooltip="Configuration particulière"
                        disabled={updating}
                        class:active={sOptions.has_pmethod === true}
                        onclick={() => {
                            sOptions.has_pmethod = sOptions.has_pmethod === true ? null : true
                            setQueryString(sOptions)
                        }}><Icon inline icon="mdi:asterisk" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary tt-below"
                        data-tooltip="Carte de crédit"
                        disabled={updating}
                        class:active={sOptions.has_pmethod === 'credit card'}
                        onclick={() => {
                            sOptions.has_pmethod = sOptions.has_pmethod === 'credit card' ? null : 'credit card'
                            setQueryString(sOptions)
                        }}><Icon inline icon="mdi:credit-card" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary tt-below"
                        data-tooltip="Stripe"
                        disabled={updating}
                        class:active={sOptions.has_pmethod === 'stripe'}
                        onclick={() => {
                            sOptions.has_pmethod = sOptions.has_pmethod === 'stripe' ? null : 'stripe'
                            setQueryString(sOptions)
                        }}><Icon inline icon="cib:stripe-s" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary tt-below"
                        data-tooltip="Soumission"
                        disabled={updating}
                        class:active={sOptions.has_pmethod === 'quotation'}
                        onclick={() => {
                            sOptions.has_pmethod = sOptions.has_pmethod === 'quotation' ? null : 'quotation'
                            setQueryString(sOptions)
                        }}><Icon inline icon="mdi:offer" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary tt-below"
                        data-tooltip="Paiement différé"
                        disabled={updating}
                        class:active={sOptions.has_pmethod === 'bill later'}
                        onclick={() => {
                            sOptions.has_pmethod = sOptions.has_pmethod === 'bill later' ? null : 'bill later'
                            setQueryString(sOptions)
                        }}><Icon inline icon="mdi:watch-later" /></button
                    >
                </div>
            </div>
            <div class="d-flex gap-2 flex-wrap mt-2">
                <!-- Signin – Passkey -->
                <div class="btn-group">
                    <button
                        type="button"
                        class="btn btn-outline-secondary tt-below"
                        data-tooltip={'Passkey'}
                        disabled={updating}
                        class:active={sOptions.has_passkey === true}
                        onclick={() => {
                            sOptions.has_passkey = sOptions.has_passkey === true ? null : true
                            setQueryString(sOptions)
                        }}><Icon inline icon="mdi:fingerprint" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary overlap tt-below"
                        data-tooltip={'Sans passkey'}
                        disabled={updating}
                        class:active={sOptions.has_passkey === false}
                        onclick={() => {
                            sOptions.has_passkey = sOptions.has_passkey === false ? null : false
                            setQueryString(sOptions)
                        }}><Icon inline icon="mdi:fingerprint" /><Icon inline icon="lucide:slash" /></button
                    >
                </div>
                <!-- Signin – Recovery -->
                <div class="btn-group">
                    <button
                        type="button"
                        class="btn btn-outline-secondary tt-below"
                        data-tooltip={'Codes de recouvrement'}
                        disabled={updating}
                        class:active={sOptions.has_recovery === true}
                        onclick={() => {
                            sOptions.has_recovery = sOptions.has_recovery === true ? null : true
                            setQueryString(sOptions)
                        }}><Icon inline icon="mdi:key" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary overlap tt-below"
                        data-tooltip={'Sans code de recouvrement'}
                        disabled={updating}
                        class:active={sOptions.has_recovery === false}
                        onclick={() => {
                            sOptions.has_recovery = sOptions.has_recovery === false ? null : false
                            setQueryString(sOptions)
                        }}><Icon inline icon="mdi:key" /><Icon inline icon="lucide:slash" /></button
                    >
                </div>

                <!-- Signin — TwoFa -->
                <div class="btn-group">
                    <button type="button" class="btn btn-secondary" disabled><Icon inline icon="mdi:two-factor-authentication" /></button>
                    <button
                        type="button"
                        class="btn btn-outline-secondary tt-below"
                        data-tooltip={'Sans authentification à deux facteurs'}
                        disabled={updating}
                        class:active={sOptions.has_twofa === false}
                        onclick={() => {
                            sOptions.has_twofa = sOptions.has_twofa === false ? null : false
                            setQueryString(sOptions)
                        }}><Icon inline icon="mdi:do-not-disturb-alt" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary tt-below"
                        data-tooltip="Authentification à deux facteurs"
                        disabled={updating}
                        class:active={sOptions.has_twofa === true}
                        onclick={() => {
                            sOptions.has_twofa = sOptions.has_twofa === true ? null : true
                            setQueryString(sOptions)
                        }}><Icon inline icon="mdi:asterisk" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary tt-below"
                        data-tooltip="Application / TOTP"
                        disabled={updating}
                        class:active={sOptions.has_twofa === 'totp'}
                        onclick={() => {
                            sOptions.has_twofa = sOptions.has_twofa === 'totp' ? null : 'totp'
                            setQueryString(sOptions)
                        }}><Icon inline icon="mdi:cellphone" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary tt-below"
                        data-tooltip="Application / SMS"
                        disabled={updating}
                        class:active={sOptions.has_twofa === 'sms'}
                        onclick={() => {
                            sOptions.has_twofa = sOptions.has_twofa === 'sms' ? null : 'sms'
                            setQueryString(sOptions)
                        }}><Icon inline icon="mdi:message-text" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary tt-below"
                        data-tooltip="Application / Courriel"
                        disabled={updating}
                        class:active={sOptions.has_twofa === 'email'}
                        onclick={() => {
                            sOptions.has_twofa = sOptions.has_twofa === 'email' ? null : 'email'
                            setQueryString(sOptions)
                        }}><Icon inline icon="mdi:email" /></button
                    >
                </div>

                <!-- API Tokens -->
                <div class="btn-group">
                    <button
                        type="button"
                        class="btn btn-outline-secondary tt-below"
                        data-tooltip={'Jeton(s) pour l’API'}
                        disabled={updating}
                        class:active={sOptions.has_api_token === true}
                        onclick={() => {
                            sOptions.has_api_token = sOptions.has_api_token === true ? null : true
                            setQueryString(sOptions)
                        }}><Icon inline icon="mdi:api" /></button
                    >
                    <button
                        type="button"
                        class="btn btn-outline-secondary overlap tt-below"
                        data-tooltip={'Sans jeton pour l’API'}
                        disabled={updating}
                        class:active={sOptions.has_api_token === false}
                        onclick={() => {
                            sOptions.has_api_token = sOptions.has_api_token === false ? null : false
                            setQueryString(sOptions)
                        }}><Icon inline icon="mdi:api-off" /></button
                    >
                </div>
            </div>
            {#if extraReady && extraMetaFields}
                <div class="search-extra-meta-container">
                    {#each extraMetaFields as emf}
                        <div class="search-extra-meta-choice">
                            {#if emf.type == 'choice'}
                                <Dropdown
                                    items={emf.choices}
                                    placeholder={emf.name}
                                    onSelect={(e) => {
                                        sOptions[`em-${emf.tag}`] = e
                                        setQueryString(sOptions)
                                    }}
                                    text={'text'}
                                    id={'tag'}
                                    selected={sOptions[`em-${emf.tag}`]}
                                    empty={''}
                                    clear={'Tout'}
                                    disabled={updating}
                                    alignRight={true}
                                    block={true}
                                />
                            {/if}
                        </div>
                    {/each}
                </div>
            {/if}
        </form>
    </div>
</div>

{#if loading}
    <div class="card">
        <div class="card-body">
            <div class="m-4 d-flex justify-content-center align-items-center">
                <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
            </div>
        </div>
    </div>
{:else}
    <div class="card">
        <div class="card-title text-primary">{$_('Users')}</div>

        <div class="card-body">
            {#if $results.length}
                <div id="wdom-user-table" class:disabled-look={updating}>
                    <div class="wut-h deleg-title">Délégués</div>
                    <div class="wut-h wut-c1">{$_('Name')}</div>
                    <div class="wut-h">{$_('Email address')}</div>
                    <div class="wut-h signopts">Connexion</div>
                    <div class="wut-h systags">Options</div>
                    <div class="wut-h tags">Tags</div>
                    <div class="wut-h">Création</div>
                    <div class="wut-h">{$_('Domains (E/Tot)')}</div>
                    <div class="wut-h">{$_('Zones (Pub/Tot)')}</div>
                    <div class="wut-h">{$_('Zone Slots (U/Tot)')}</div>
                    <div class="wut-h"><span class="min">est</span> <span class="extra">délégué</span></div>
                    <div class="wut-h"><span class="min">a des</span> <span class="extra">délégués</span></div>
                    <div class="wut-h"><Icon inline icon="mdi:account" /></div>
                    {#each $results as item, index (item.id)}
                        <div class="wut-c1" class:row-odd={index % 2 == 1} class:row-even={index % 2 == 0}>
                            <a use:sanchor href={spath(`/wdom-user/${item.id}`)}>{item.metadata && item.metadata.name ? item.metadata.name : '<not set>'}</a>
                        </div>
                        <div class:row-odd={index % 2 == 1} class:row-even={index % 2 == 0}>
                            {item.email}
                        </div>
                        <div class="signopts" class:row-odd={index % 2 == 1} class:row-even={index % 2 == 0}>
                            <span>
                                {#if item.n_passkeys > 0}
                                    <span class=""><Icon inline icon="mdi:cellphone" /></span>
                                {:else}
                                    <span class="text-very-muted systag-empty overlap"><Icon inline icon="mdi:cellphone" /><Icon inline icon="lucide:slash" /></span>
                                {/if}
                                {#if item.n_recovery_codes > 0}
                                    <span><Icon inline icon="mdi:key" /></span>
                                {:else}
                                    <span class="text-very-muted systag-empty overlap"><Icon inline icon="mdi:key" /><Icon inline icon="lucide:slash" /></span>
                                {/if}
                                {#if item.n_twofa_totp > 0}
                                    <span><Icon inline icon="mdi:fingerprint" /></span>
                                {:else}
                                    <span class="text-very-muted systag-empty overlap"><Icon inline icon="lucide-lab:slot-card-credit" /><Icon inline icon="mdi:fingerprint" /></span>
                                {/if}
                                {#if item.n_twofa_sms > 0}
                                    <span><Icon inline icon="mdi:message-text" /></span>
                                {:else}
                                    <span class="text-very-muted systag-empty overlap"><Icon inline icon="lucide-lab:slot-card-credit" /><Icon inline icon="mdi:message-text" /></span>
                                {/if}
                                {#if item.n_twofa_email > 0}
                                    <span><Icon inline icon="mdi:email" /></span>
                                {:else}
                                    <span class="text-very-muted systag-empty overlap"><Icon inline icon="lucide-lab:slot-card-credit" /><Icon inline icon="mdi:email" /></span>
                                {/if}
                                {#if item.n_api_tokens > 0}
                                    <span class="" data-tooltip={$ngettext('{n} jeton', '{n} jetons', { n: item.n_api_tokens })}><Icon inline icon="mdi:api" /></span>
                                {:else}
                                    <span class="text-very-muted systag-empty overlap"><Icon inline icon="mdi:api-off" /></span>
                                {/if}
                            </span>
                        </div>
                        <div class="systags" class:row-odd={index % 2 == 1} class:row-even={index % 2 == 0}>
                            <span>
                                {#if item.tax_exempted}
                                    <span class="overlap"><Icon inline icon="tabler:tax" /><Icon inline icon="lucide:slash" /></span>
                                {:else}
                                    <span class="text-very-muted systag-empty"><Icon inline icon="tabler:tax" /></span>
                                {/if}
                                {#if item.has_custom_zone_price}
                                    <span><Icon inline icon="mdi:database" /></span>
                                {:else}
                                    <span class="text-very-muted systag-empty overlap"><Icon inline icon="mdi:database" /><Icon inline icon="lucide:slash" /></span>
                                {/if}
                                {#if item.has_admin_zone_slots}
                                    <span><Icon inline icon="lucide-lab:slot-card-credit" /></span>
                                {:else}
                                    <span class="text-very-muted systag-empty overlap"><Icon inline icon="lucide-lab:slot-card-credit" /><Icon inline icon="lucide:slash" /></span>
                                {/if}
                                {#if item.tld_with_custom_price && item.tld_with_custom_price.length}
                                    <span data-tooltip={makeTldList(item.tld_with_custom_price)}><Icon inline icon="fa:globe" /></span>
                                {:else}
                                    <span class="text-very-muted systag-empty overlap"><Icon inline icon="fa:globe" /><Icon inline icon="lucide:slash" /></span>
                                {/if}
                                {#if item.payment_types && item.payment_types.length}
                                    <span data-tooltip={makeMethodList(item.payment_types)}><Icon inline icon="mdi:dollar" /></span>
                                {:else}
                                    <span class="text-very-muted systag-empty"><Icon inline icon="mdi:dollar" /></span>
                                {/if}
                            </span>
                        </div>
                        <div class="tags" class:row-odd={index % 2 == 1} class:row-even={index % 2 == 0}>
                            {#if item.tags && item.tags.length}
                                {#each item.tags as tag}
                                    <span class="enum"><span>{tag}</span></span>
                                {/each}
                            {:else}
                                –
                            {/if}
                        </div>
                        <div class:row-odd={index % 2 == 1} class:row-even={index % 2 == 0}>
                            <Date value={item.ctime} format="h-table-date" />
                        </div>
                        <div class:row-odd={index % 2 == 1} class:row-even={index % 2 == 0}>
                            {item.near_expiration_count != null ? item.near_expiration_count : 0} / {item.domain_count != null ? item.domain_count : 0}
                        </div>
                        <div class:row-odd={index % 2 == 1} class:row-even={index % 2 == 0}>
                            {item.n_published != null ? item.n_published : 0} / {item.n_zones != null ? item.n_zones : 0}
                        </div>
                        <div class:row-odd={index % 2 == 1} class:row-even={index % 2 == 0}>
                            {item.zone_n_inuse} / {item.zone_n_avail}
                        </div>
                        <div class:row-odd={index % 2 == 1} class:row-even={index % 2 == 0}>
                            <span class:text-very-muted={!item.is_technical_delegate}><span class="min">T</span><span class="extra">ech</span></span>
                            <span class:text-very-muted={!item.is_renewal_delegate}><span class="min">R</span><span class="extra">enew</span></span>
                        </div>
                        <div class:row-odd={index % 2 == 1} class:row-even={index % 2 == 0}>
                            <span class:text-very-muted={!item.has_technical_delegates}><span class="min">T</span><span class="extra">ech</span></span>
                            <span class:text-very-muted={!item.has_renewal_delegates}><span class="min">R</span><span class="extra">enew</span></span>
                        </div>
                        <div class:row-odd={index % 2 == 1} class:row-even={index % 2 == 0}>
                            <span class="tt-left" class:text-very-muted={!item.has_subaccounts && !item.is_subaccount} class:is-subaccount={item.is_subaccount} class:has-subaccounts={item.has_subaccounts} data-tooltip={item.is_subaccount ? 'Est sous-compte' : item.has_subaccounts ? 'A des sous-comptes' : null}><Icon inline icon={item.has_subaccounts ? 'pajamas:work-item-parent' : item.is_subaccount ? 'pajamas:work-item-child' : 'mdi:account'} /></span>
                        </div>
                    {/each}
                </div>
            {:else}
                <div class="p-3 text-center">{$_('No results')}</div>
            {/if}
        </div>
        <div class="card-footer text-bg-light">
            <PageList
                loading={updating}
                {nItems}
                limit={LIMIT}
                skip={sOptions.skip}
                setSkip={(e) => {
                    sOptions.skip = e
                    setQueryString(sOptions)
                }}
            />
        </div>
        <div class="card-footer text-bg-light"></div>
    </div>

    <div class="card mt-4">
        <div class="card-title text-primary">{$_('Rapports TSV et outils')}</div>
        <div class="card-body d-flex gap-2 flex-wrap">
            <div class="btn-group"><a use:sanchor href={spath('wdom-user/new')} class="btn btn-primary">{$_('Create a new user')}</a></div>
            {#if $ugroups.has('wdom-user-admin') && $ugroups.has('global-ecom-admin')}
                <div class="btn-group">
                    <button type="button" disabled class="btn btn-outline-secondary">Liste d’utilisateurs (TSV) :</button>
                    <TokenAnchorProgress class="btn btn-outline-secondary" href={`/json/wdom-user/1/all-users/tsv?data=${searchQueryStringJson}`}>
                        Tous{#if searchQueryStringJson != '%7B%7D'}<sup>*</sup>{/if}
                    </TokenAnchorProgress>
                    <TokenAnchorProgress class="btn btn-outline-secondary" href="/json/wdom-user/1/all-deleted-users/tsv">Détruits</TokenAnchorProgress>
                </div>
                <div class="btn-group"><a use:sanchor href={spath(`/wdom-user/extra-meta/batch?sparams=${searchQueryStringJson}`)} class="btn btn-outline-secondary">Méta-données en vrac<sup>*, **</sup></a></div>
                <div class="small" style="flex: 1 0 100%"><sup>*</sup> : Basé sur la recherche de la liste ci-haut.</div>
                <div class="small" style="flex: 1 0 100%"><sup>**</sup> : Pour que « suivant » fonctionne correctement,s’assurer d’ajouter un champ avec « Sans réponse »</div>
            {/if}
        </div>
    </div>
{/if}

{#if showFilters}
    <ExtraFilters
        close={() => (showFilters = false)}
        filter={(e) => {
            showFilters = false
            $text = e
            submit()
        }}
    />
{/if}
